'use client'

import { RefObject, useEffect } from 'react'

import { TSearchFilter, useSearchFilterStore } from '@/entities/map'
import { Filter, useFilterOptions } from '@/features/room-filter'
import { cn } from '@/shared/lib'

import { KeywordSearchFilterOverlayTrigger } from '../filter-overlay'

type FilterMenuItemProps = {
  title: string
  type: ReturnType<typeof useFilterOptions>['FILTER_OPTIONS'][number]['type']
  field: (keyof TSearchFilter)[]
  width: number
}

const FilterMenuItem = ({ title, type, field, width }: FilterMenuItemProps) => {
  const isChanged = useSearchFilterStore(state => state.hasChanged(field))

  return (
    <KeywordSearchFilterOverlayTrigger target={type}>
      <Filter.Item width={width} selected={isChanged}>
        {title}
      </Filter.Item>
    </KeywordSearchFilterOverlayTrigger>
  )
}

type Props = {
  ref?: RefObject<HTMLDivElement | null>
  isListScrolled?: boolean
  className?: string
}

export const MobileFilterMenuList = ({
  ref,
  isListScrolled = false,
  className,
}: Props) => {
  const { FILTER_OPTIONS } = useFilterOptions()

  const handleTouch = (event: TouchEvent) => {
    event.stopPropagation()
  }

  useEffect(() => {
    const element = ref?.current
    if (!element) return

    element.addEventListener('touchstart', handleTouch, { passive: true })
    element.addEventListener('touchmove', handleTouch, { passive: true })
    element.addEventListener('touchend', handleTouch, { passive: true })

    return () => {
      element.removeEventListener('touchstart', handleTouch)
      element.removeEventListener('touchmove', handleTouch)
      element.removeEventListener('touchend', handleTouch)
    }
  }, [ref])

  return (
    <div
      className={cn(
        'relative flex gap-1.5 px-4 pb-3',
        isListScrolled && 'shadow-[0_0_10px_0_rgba(0,0,0,0.15)]',
        className,
      )}
      ref={ref}
    >
      <KeywordSearchFilterOverlayTrigger>
        <Filter.All />
      </KeywordSearchFilterOverlayTrigger>
      <div
        className={cn(
          'flex gap-1.5',
          'touch-pan-x overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {FILTER_OPTIONS.map(option => (
          <FilterMenuItem key={option.type} {...option} />
        ))}
      </div>
    </div>
  )
}
